import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { DAILY_CHALLENGES } from "@/lib/content";
import { sounds } from "@/lib/sounds";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { useSmartBack } from "@/hooks/use-smart-back";

export const Route = createFileRoute("/_authenticated/app/child/$childId/challenges")({
  component: Challenges,
});

function Challenges() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/child/$childId", params: { childId } }));
  const { lang, t } = useLang();
  const [done, setDone] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    supabase
      .from("progress_events")
      .select("activity_id")
      .eq("child_id", childId)
      .eq("activity_type", "challenge")
      .gte("created_at", today.toISOString())
      .then(({ data }) => setDone(new Set((data ?? []).map((d) => d.activity_id ?? ""))));
  }, [childId]);

  const complete = async (id: string, points: number, label: string) => {
    if (done.has(id)) return;
    setBusy(id);
    // Challenge complete is silent — no reward chime (docs/AUDIO_PLAN.md §5).
    sounds.tap();
    await supabase.from("progress_events").insert({
      child_id: childId,
      activity_type: "challenge",
      activity_id: id,
      points_earned: points,
    });
    setDone(new Set([...done, id]));
    toast.success(`+${points} · ${label}`);
    setBusy(null);
  };

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto">
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> {t("backToHome")}
      </button>
      <h1 className="font-display-en text-3xl font-bold tracking-tight mb-1">{t("challenges")}</h1>
      <p className="text-foreground/60 mb-6 text-sm">
        Complete them daily to build your streak 🔥
      </p>

      <div className="space-y-3">
        {DAILY_CHALLENGES.map((c) => {
          const isDone = done.has(c.id);
          return (
            <button
              key={c.id}
              onClick={() => complete(c.id, c.points, lang === "ar" ? c.ar : c.en)}
              disabled={isDone || busy === c.id}
              className={`w-full text-left bg-card border-2 rounded-3xl p-5 transition-all ${
                isDone
                  ? "border-secondary/60 bg-secondary/5"
                  : "border-clay/30 hover:border-primary hover:-translate-y-0.5"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl">{c.emoji}</div>
                <div className="flex-1">
                  <p className="font-display-ar text-lg text-primary mb-0.5" dir="rtl">
                    {c.ar}
                  </p>
                  <h3 className="font-bold text-sm mb-1">{c.en}</h3>
                  <p className="text-xs text-foreground/60">
                    {lang === "ar" ? c.desc_ar : c.desc_en}
                  </p>
                </div>
                <div className="text-right">
                  {isDone ? (
                    <div className="size-10 rounded-full bg-secondary/20 text-secondary flex items-center justify-center">
                      <Check className="size-5" />
                    </div>
                  ) : (
                    <span className="font-display-en text-lg font-black text-primary">
                      +{c.points}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
